import type { ReactNode } from "react";
import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";
import { formatPct } from "@cx/domain";

type Props = {
  label: string;
  /** Valor já formatado ("R$ 1.234,56"); "—" quando o servidor não soma com segurança. */
  value: ReactNode;
  /** Variação contra o período anterior em pct4 (10.000 = 1%); null = sem base de comparação. */
  variationPct4?: number | null;
  /** Valor do período anterior já formatado (vai no rodapé). */
  previous?: ReactNode;
  /** Quando subir é ruim (custo, imposto): inverte as cores. */
  inverse?: boolean;
  /** Linha de apoio abaixo do valor (ex.: aviso de custo incompleto). */
  hint?: ReactNode;
  testId?: string;
};

/** Sentido da variação para cor e leitor de tela: alta, queda ou estável (sem base = null). */
export function variationTone(pct4: number | null | undefined, inverse = false) {
  if (pct4 === null || pct4 === undefined) return null;
  if (pct4 === 0) return { word: "estável", tone: "secondary", Icon: ArrowRight } as const;
  const up = pct4 > 0;
  const good = up !== inverse;
  return {
    word: up ? "alta" : "queda",
    tone: good ? "success" : "danger",
    Icon: up ? ArrowUpRight : ArrowDownRight,
  } as const;
}

/** KPI do dashboard (small-box do Coup): valor do período e variação contra o anterior (RF-BI-01/02). */
export function KpiCard({ label, value, variationPct4, previous, inverse = false, hint, testId }: Props) {
  const v = variationTone(variationPct4, inverse);
  return (
    <div className="box card h-100 cx-kpi" data-testid={testId}>
      <div className="card-body">
        <p className="text-muted small mb-1">{label}</p>
        <p className="h4 mb-1 fw-semibold text-nowrap" data-kpi-value>
          {value}
        </p>
        {hint && <p className="small text-warning-emphasis mb-1">{hint}</p>}
        <p className="small mb-0 text-muted">
          {v ? (
            <span className={`text-${v.tone}-emphasis fw-semibold me-1`}>
              <v.Icon size={14} aria-hidden="true" /> <span className="visually-hidden">{v.word} de </span>
              {formatPct(Math.abs(variationPct4!))}
            </span>
          ) : (
            <span className="me-1">—</span>
          )}
          {previous !== undefined && <span>vs. {previous}</span>}
        </p>
      </div>
    </div>
  );
}
